/* =============================================================================
 * PROTON — BACK IN STOCK capture (PDP)
 * Built 2026-09-09. Source of truth: ~/proton/scripts/backinstock/
 * Deployed as theme asset `assets/proton-backinstock.js`.
 *
 * WHAT DIEGO ASKED FOR (2026-09-09):
 *   "when people click on the peacock, the flamingo, or any bats, if any product is marked
 *    out of stock, I want a pop-up to come up when they go into the actual products page…
 *    'Sign up to get notifications … when it's back in stock'… It should be a feature that
 *    you automatically have where you can detect when a product is marked out of stock to
 *    put that pop-up in, and then, when it's marked back in stock, get rid of the pop-up."
 *
 * ⭐ SO THERE IS NO PER-PRODUCT CONFIGURATION AND NOTHING TO TURN OFF LATER.
 *   Availability is read LIVE on every page view and the UI is a pure function of it.
 *   Restock the variant in Shopify and the popup and button stop existing on their own —
 *   not because a job ran, but because there is no stored state to go stale.
 *
 * 🚨 WHY WE FETCH `/products/<handle>.js` INSTEAD OF READING THE RENDERED PAGE
 *   Shopify's full-page cache serves stale PDP HTML for up to ~60 minutes (see memory:
 *   storefront-soldout-dim). A sold-out badge baked into cached HTML is NOT proof of
 *   current stock, and the failure is the bad direction: we would ask people to sign up
 *   for a restock alert on a paddle that is already back on the shelf and buyable.
 *
 * 🚨 VARIANT-LEVEL, NOT PRODUCT-LEVEL. A bat is 6–9 variants and a hoodie is 60. "Out of
 *   stock" is almost never the whole product — measured 2026-09-09: 14 products fully out,
 *   26 more partially out. Asking someone to wait for a restock on a size that IS in stock
 *   is worse than showing nothing.
 *
 * 🛑 WE ONLY EVER ASK ABOUT THINGS THAT CAN COME BACK.
 *   `availableForSale` already excludes pre-orders and continue-selling items (Project
 *   Coyote sells while out of stock, so it never triggers). The `no-restock` TAG is the
 *   manual escape hatch for discontinued lines — see DISCONTINUED_TAG below.
 * ============================================================================= */
(function () {
  "use strict";

  /* ── configuration ──────────────────────────────────────────────────────── */
  var KLAVIYO_COMPANY_ID = "SntER6";
  var KLAVIYO_LIST_ID = "Vj7gqn"; // "Back In Stock Requests (2026-09-09)" — SINGLE opt-in
  var KLAVIYO_REVISION = "2024-10-15";
  var METRIC = "Back In Stock Requested";

  // 🛑 Tag a product `no-restock` when it is DISCONTINUED, not merely sold out. Collecting
  // restock alerts for something that is never coming back is a promise we cannot keep, and
  // the person finds out by waiting forever. (Flagged 2026-09-09: Project Flamingo is fully
  // out across all four SKUs and its manufacturing reportedly ceased — confirm before
  // letting it collect signups.)
  var DISCONTINUED_TAG = "no-restock";

  var STORE_KEY = "proton_bis_v1";
  var MODAL_COOLDOWN_DAYS = 30; // per product, per browser

  // 🚨 THE MODAL MUST ARRIVE AFTER THEY WORK OUT IT IS SOLD OUT, NOT BEFORE.
  // Diego, 2026-09-09: "the pop-up … comes too soon. It needs to be delayed so that they
  // can actually get a chance to see that it's out of stock."
  // At the original 1200ms the modal landed while the PDP was still painting, so it read as
  // a generic email grab that happened to be in the way — the shopper had not yet formed
  // the "damn, it's gone" feeling the offer is an answer to. The whole value of this prompt
  // is that it shows up as a SOLUTION to a problem they just noticed.
  // 🛑 Do not tune this back down to chase capture rate; interrupting before the problem
  //    lands converts worse AND annoys more.
  var MODAL_DELAY_MS = 7000;
  // The inline buy-box button still mounts IMMEDIATELY. It is not an interruption — it
  // reinforces the sold-out state at exactly the moment they look for the Add to Cart
  // button, and it means the offer is never lost while we wait out the delay.

  var ORANGE = "#ff6000";

  var qs = new URLSearchParams(location.search);
  var DEBUG = qs.get("protonbis") === "debug";
  var FORCE = qs.get("protonbis") === "force" || DEBUG;
  var log = function () {
    if (DEBUG) console.log.apply(console, ["[bis]"].concat([].slice.call(arguments)));
  };

  /* ── state ──────────────────────────────────────────────────────────────── */
  var product = null; // live product JSON
  var currentVariantId = null;
  var modalShownThisPage = false;

  function readState() {
    try {
      return JSON.parse(localStorage.getItem(STORE_KEY) || "{}") || {};
    } catch (e) {
      return {};
    }
  }
  function writeState(patch) {
    try {
      var s = readState();
      for (var k in patch) s[k] = patch[k];
      localStorage.setItem(STORE_KEY, JSON.stringify(s));
    } catch (e) {}
  }

  function handleFromPath() {
    var m = location.pathname.match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  }

  /* ── live availability ──────────────────────────────────────────────────── */
  function loadProduct(handle) {
    // `cache:'no-store'` matters: without it the browser can serve its own cached copy of
    // the .js endpoint and we are back to reasoning about stale stock.
    return fetch("/products/" + handle + ".js", { cache: "no-store", credentials: "same-origin" })
      .then(function (r) {
        if (!r.ok) throw new Error("product fetch " + r.status);
        return r.json();
      });
  }

  function variantById(id) {
    if (!product) return null;
    for (var i = 0; i < product.variants.length; i++) {
      if (String(product.variants[i].id) === String(id)) return product.variants[i];
    }
    return null;
  }

  // Which variant is the customer actually looking at? In order of reliability:
  //   1. ?variant= in the URL (Dawn writes it on every selection)
  //   2. the product form's hidden id input
  //   3. the first available variant, else the first variant — matching Shopify's own
  //      default selection, so we never disagree with the Add-to-Cart button.
  function detectVariantId() {
    var v = new URLSearchParams(location.search).get("variant");
    if (v && variantById(v)) return v;
    var input = document.querySelector(
      'form[action*="/cart/add"] [name="id"], form[action*="/cart/add"] input[name="id"]'
    );
    if (input && input.value && variantById(input.value)) return input.value;
    if (!product) return null;
    for (var i = 0; i < product.variants.length; i++) {
      if (product.variants[i].available) return String(product.variants[i].id);
    }
    return String(product.variants[0].id);
  }

  function buyArea() {
    var b = document.querySelector(".product-form__submit");
    var area = b && (b.closest(".product-form__buttons") || b.form);
    return area || document.querySelector("product-form") || document.querySelector(".product-form");
  }

  function isComingSoon() {
    return !!product && (product.tags || []).some(function (t) { return String(t).toLowerCase() === "coming-soon"; });
  }

  function isExcluded() {
    if (!product) return "no product";
    var tags = (product.tags || []).map(function (t) {
      return String(t).toLowerCase();
    });
    if (tags.indexOf(DISCONTINUED_TAG) !== -1) return "tagged " + DISCONTINUED_TAG;
    if (String(product.type || "").toLowerCase() === "gift card") return "gift card";
    // Another Proton overlay owns the screen — never stack two modals.
    if (document.querySelector("#pwp-ov, #proton-cleaner-ov")) return "another overlay is open";
    return null;
  }

  /* ── styles ─────────────────────────────────────────────────────────────── */
  function injectCSS() {
    if (document.getElementById("pbis-style")) return;
    var s = document.createElement("style");
    s.id = "pbis-style";
    s.textContent = [
      /* inline buy-box button — the permanent affordance */
      "#pbis-inline{display:block;width:100%;box-sizing:border-box;margin:10px 0 0;padding:15px 18px;",
      "border-radius:12px;border:1px solid rgba(255,96,0,.55);background:rgba(255,96,0,.10);",
      "color:inherit;font:inherit;font-size:15px;font-weight:700;cursor:pointer;text-align:center;",
      "transition:background .18s,border-color .18s}",
      "#pbis-inline:hover{background:rgba(255,96,0,.18);border-color:" + ORANGE + "}",
      "#pbis-inline .pbis-dot{display:inline-block;width:8px;height:8px;border-radius:50%;",
      "background:" + ORANGE + ";margin-right:9px;vertical-align:middle}",
      /* modal */
      "#pbis-ov{position:fixed;inset:0;z-index:2147482900;display:flex;align-items:center;justify-content:center;",
      "padding:18px;background:rgba(8,8,10,.72);-webkit-backdrop-filter:blur(10px);backdrop-filter:blur(10px);",
      "opacity:0;transition:opacity .26s ease;",
      "font-family:var(--font-body-family,ui-sans-serif,system-ui,-apple-system,'Helvetica Neue',Arial,sans-serif)}",
      "#pbis-ov.pbis-in{opacity:1}",
      "#pbis-card{position:relative;width:100%;max-width:430px;box-sizing:border-box;padding:30px 26px 26px;",
      "border-radius:22px;color:#f4f2ee;overflow:hidden;",
      "background:linear-gradient(158deg,#17171d 0%,#0b0b0d 62%);",
      "border:1px solid rgba(255,255,255,.12);",
      "box-shadow:0 40px 90px -20px rgba(0,0,0,.75),0 0 70px -30px rgba(255,96,0,.55)}",
      // Same lesson as the welcome popup: keep decorative glows INSIDE the box or they
      // become real overflow and the card grows a scrollbar.
      "#pbis-card::before{content:'';position:absolute;inset:0;pointer-events:none;",
      "background:radial-gradient(58% 55% at 86% 8%,rgba(255,96,0,.28),transparent 70%)}",
      "#pbis-card>*{position:relative;z-index:1}",
      "#pbis-x{position:absolute;top:10px;right:10px;z-index:3;width:38px;height:38px;display:grid;place-items:center;",
      "border-radius:50%;border:1px solid rgba(255,255,255,.18);background:rgba(12,12,14,.55);color:#fff;",
      "cursor:pointer;font-size:17px;line-height:1;transition:background .18s,transform .18s}",
      "#pbis-x:hover{background:rgba(255,96,0,.85);transform:rotate(90deg)}",
      ".pbis-eyebrow{display:flex;align-items:center;gap:9px;margin:0 0 12px;font-size:10.5px;letter-spacing:.18em;",
      "text-transform:uppercase;font-weight:700;color:" + ORANGE + "}",
      ".pbis-eyebrow i{display:block;width:22px;height:2px;background:" + ORANGE + ";border-radius:2px}",
      "#pbis-title{margin:0 0 9px;font-size:25px;line-height:1.12;letter-spacing:-.02em;font-weight:800;color:#fff;",
      "font-family:var(--font-heading-family,inherit)}",
      "#pbis-sub{margin:0 0 18px;font-size:14px;line-height:1.5;color:rgba(244,242,238,.75)}",
      "#pbis-sub b{color:#fff;font-weight:700}",
      ".pbis-prod{display:flex;align-items:center;gap:12px;margin:0 0 18px;padding:10px;border-radius:13px;",
      "background:rgba(255,255,255,.05);border:1px solid rgba(255,255,255,.10)}",
      ".pbis-prod img{width:52px;height:52px;object-fit:cover;border-radius:9px;background:#fff;flex:none}",
      ".pbis-prod div{min-width:0}",
      ".pbis-prod strong{display:block;font-size:13.5px;color:#fff;font-weight:700;line-height:1.25;",
      "overflow:hidden;text-overflow:ellipsis;white-space:nowrap}",
      ".pbis-prod span{display:block;font-size:12px;color:rgba(244,242,238,.6);margin-top:2px}",
      ".pbis-field{position:relative;margin:0 0 10px}",
      "#pbis-email{width:100%;box-sizing:border-box;height:54px;padding:21px 15px 7px;font-size:16px;color:#fff;",
      "background:rgba(255,255,255,.06);border:1px solid rgba(255,255,255,.16);border-radius:13px;outline:none;",
      "-webkit-appearance:none;appearance:none;transition:border-color .18s,box-shadow .18s}",
      "#pbis-email:focus{border-color:" + ORANGE + ";box-shadow:0 0 0 4px rgba(255,96,0,.16)}",
      ".pbis-field label{position:absolute;left:16px;top:17px;font-size:14.5px;color:rgba(244,242,238,.5);",
      "pointer-events:none;transition:all .16s ease}",
      "#pbis-email:focus~label,#pbis-email.pbis-has~label{top:8px;font-size:10px;letter-spacing:.12em;",
      "text-transform:uppercase;color:rgba(244,242,238,.62)}",
      "#pbis-err{display:block;min-height:16px;margin:0 0 8px;font-size:12px;color:#ff8a7d}",
      "#pbis-submit{width:100%;height:52px;border:0;border-radius:13px;cursor:pointer;color:#fff;",
      "font-size:15.5px;font-weight:800;font-family:inherit;",
      "background:linear-gradient(135deg,#ff8a2b 0%," + ORANGE + " 52%,#e04e00 100%);",
      "box-shadow:0 10px 30px -8px rgba(255,96,0,.7);transition:transform .14s,box-shadow .2s,filter .2s}",
      "#pbis-submit:hover{transform:translateY(-1px)}",
      "#pbis-submit[disabled]{filter:saturate(.5) brightness(.8);cursor:default;transform:none}",
      ".pbis-fine{margin:12px 0 0;font-size:11px;line-height:1.45;color:rgba(244,242,238,.45)}",
      ".pbis-fine a{color:rgba(244,242,238,.68);text-decoration:underline}",
      "#pbis-done{display:none;text-align:center;padding:6px 0 2px}",
      "#pbis-card.pbis-done #pbis-form{display:none}",
      "#pbis-card.pbis-done #pbis-done{display:block}",
      "#pbis-done .pbis-tick{width:54px;height:54px;margin:0 auto 14px;border-radius:50%;display:grid;place-items:center;",
      "background:rgba(255,96,0,.16);border:1px solid rgba(255,96,0,.6);color:" + ORANGE + ";font-size:26px}",
      "@media (prefers-reduced-motion:reduce){#pbis-ov,#pbis-submit,#pbis-x{transition:none!important}}",
    ].join("");
    document.head.appendChild(s);
  }

  /* ── inline buy-box button ──────────────────────────────────────────────── */
  function removeInline() {
    var el = document.getElementById("pbis-inline");
    if (el) el.remove();
  }

  function mountInline(variant) {
    if (isComingSoon()) return mountComingSoon();
    // Rebuilt on every availability change rather than toggled — the DOM then cannot
    // disagree with stock, which is the whole "it disappears by itself" requirement.
    removeInline();
    // 🐛 2026-10-06: the FIRST form[action*="/cart/add"] on the PDP is the HIDDEN Shop Pay installments
    //    form (display:none) — anything appended there is invisible. Anchor to the real buy button.
    var form = buyArea();
    if (!form) return;
    var btn = document.createElement("button");
    btn.id = "pbis-inline";
    btn.type = "button";
    btn.innerHTML = '<span class="pbis-dot"></span>' + (isComingSoon() ? 'Notify me when it drops' : 'Notify me when it\'s back in stock');
    btn.addEventListener("click", function () {
      openModal(variant, "inline");
    });
    form.appendChild(btn);
  }

  /* ── modal ──────────────────────────────────────────────────────────────── */
  var ov, card, input, err, submit, lastFocus, escHandler;


  /* ── COMING SOON inline signup (2026-10-06, Diego) ──────────────────────────
   * On a `coming-soon` product the buy button reads "Coming Soon" (theme Liquid). Instead of a
   * small "notify me" link + modal, put the email field RIGHT THERE under the button — one step.
   * Same Klaviyo list + same "Back In Stock Requested" event (so the restock/launch notifier can
   * serve it), $source "Coming Soon". Mounted ONCE per page so a variant switch never wipes a
   * half-typed email.
   * ─────────────────────────────────────────────────────────────────────── */
  function mountComingSoon() {
    if (document.getElementById("pcs-card")) return;
    removeInline();
    // 🐛 2026-10-06: the FIRST form[action*="/cart/add"] on the PDP is the HIDDEN Shop Pay installments
    //    form (display:none) — anything appended there is invisible. Anchor to the real buy button.
    var form = buyArea();
    if (!form) return;
    if (!document.getElementById("pcs-css")) {
      var st = document.createElement("style"); st.id = "pcs-css";
      st.textContent = [
        "#pcs-card{margin:14px 0 4px;padding:20px 18px 18px;border-radius:18px;background:linear-gradient(160deg,rgba(255,255,255,.92),rgba(234,243,251,.92));",
        "border:1px solid rgba(0,130,202,.22);box-shadow:0 10px 30px rgba(0,40,86,.10),0 0 0 1px rgba(255,255,255,.6) inset;font-family:inherit;color:#0b1f33}",
        "#pcs-card .pcs-eye{display:inline-flex;align-items:center;gap:7px;font-size:11px;font-weight:800;letter-spacing:.14em;text-transform:uppercase;color:#0082CA;margin:0 0 6px}",
        "#pcs-card .pcs-eye i{width:7px;height:7px;border-radius:50%;background:#0082CA;box-shadow:0 0 0 0 rgba(0,130,202,.6);animation:pcsPulse 1.8s infinite}",
        "@keyframes pcsPulse{0%{box-shadow:0 0 0 0 rgba(0,130,202,.55)}70%{box-shadow:0 0 0 9px rgba(0,130,202,0)}100%{box-shadow:0 0 0 0 rgba(0,130,202,0)}}",
        "#pcs-card h3{margin:0 0 4px;font-size:20px;line-height:1.2;font-weight:800;color:#002856;letter-spacing:-.01em}",
        "#pcs-card p.pcs-sub{margin:0 0 14px;font-size:14px;line-height:1.45;color:#3b4a5c}",
        "#pcs-card form{display:flex;gap:8px;flex-wrap:wrap}",
        "#pcs-card input{flex:1 1 200px;min-width:0;height:50px;padding:0 14px;border-radius:12px;border:1.5px solid rgba(0,40,86,.22);background:#fff;font-size:16px;color:#0b1f33;outline:none}",
        "#pcs-card input:focus{border-color:#0082CA;box-shadow:0 0 0 4px rgba(0,130,202,.15)}",
        "#pcs-card button{flex:0 0 auto;height:50px;padding:0 22px;border:0;border-radius:12px;cursor:pointer;font-size:15px;font-weight:800;letter-spacing:.04em;color:#fff;",
        "background:linear-gradient(135deg,#002856,#0082CA);box-shadow:0 8px 22px rgba(0,130,202,.35)}",
        "#pcs-card button:disabled{opacity:.7;cursor:default}",
        "#pcs-card .pcs-err{display:block;min-height:16px;margin-top:6px;font-size:12.5px;color:#c0392b}",
        "#pcs-card .pcs-fine{margin:4px 0 0;font-size:11.5px;color:#6b7785}",
        "#pcs-card .pcs-done{display:none;font-size:15px;line-height:1.45;color:#0b1f33}",
        "#pcs-card.is-done form,#pcs-card.is-done .pcs-fine,#pcs-card.is-done .pcs-err,#pcs-card.is-done p.pcs-sub{display:none}",
        "#pcs-card.is-done .pcs-done{display:block}",
        "@media (max-width:420px){#pcs-card button{flex:1 1 100%}}",
      ].join("");
      document.head.appendChild(st);
    }
    var name = String(product.title || "").split("·")[0].trim();
    var card = document.createElement("div");
    card.id = "pcs-card";
    card.innerHTML =
      '<p class="pcs-eye"><i></i>Coming soon</p>' +
      "<h3>Be first when it drops.</h3>" +
      '<p class="pcs-sub">Drop your email and we&rsquo;ll tell you the minute <b>' + esc(name) +
      "</b> is live &mdash; before it goes out to everyone else.</p>" +
      '<form novalidate><input type="email" name="email" autocomplete="email" inputmode="email" spellcheck="false" ' +
      'placeholder="Email address" aria-label="Email address" required><button type="submit">Notify me</button></form>' +
      '<span class="pcs-err" role="alert"></span>' +
      '<p class="pcs-fine">Launch alert + Proton news. Unsubscribe any time. <a href="/policies/privacy-policy">Privacy</a>.</p>' +
      '<div class="pcs-done">&#10003; <b>You&rsquo;re on the list.</b> We&rsquo;ll email you the moment ' + esc(name) + " drops.</div>";
    form.appendChild(card);
    var f = card.querySelector("form"), inp = card.querySelector("input"), btn = card.querySelector("button"), er = card.querySelector(".pcs-err");
    var st0 = readState();
    if (st0.asked && st0.asked[product.handle + ":cs"]) card.classList.add("is-done");
    f.addEventListener("submit", function (e) {
      e.preventDefault();
      var email = (inp.value || "").trim();
      if (!EMAIL_RE.test(email)) { er.textContent = "Please enter a valid email address."; inp.focus(); return; }
      er.textContent = ""; btn.disabled = true; btn.textContent = "Saving…";
      var variant = variantById(currentVariantId || detectVariantId());
      var props = {
        ProductTitle: product.title, ProductHandle: product.handle, ProductID: String(product.id),
        VariantID: variant ? String(variant.id) : null, VariantTitle: variant ? variant.title : null,
        Price: variant ? variant.price / 100 : null, ComingSoon: true,
        ProductURL: location.origin + "/products/" + product.handle,
        ImageURL: product.featured_image || null, RequestedAt: new Date().toISOString(),
      };
      Promise.allSettled([
        klSubscribe(email, KLAVIYO_LIST_ID, "Coming Soon", { bis_last_product: product.handle, coming_soon: product.handle }),
        klTrack(METRIC, email, props),
      ]).then(function (r) {
        log("coming-soon subscribe", r[0].status, "| event", r[1].status);
        if (r[0].status !== "fulfilled" && r[1].status !== "fulfilled") {
          btn.disabled = false; btn.textContent = "Notify me"; er.textContent = "Something went wrong — please try again."; return;
        }
        var stt = readState(); var asked = stt.asked || {}; asked[product.handle + ":cs"] = { at: Date.now(), signed: true }; writeState({ asked: asked });
        card.classList.add("is-done");
        try { document.dispatchEvent(new CustomEvent("proton:bis-signup", { detail: { handle: product.handle, comingSoon: true } })); } catch (x) {}
      });
    });
  }

  function closeModal() {
    if (!ov) return;
    ov.classList.remove("pbis-in");
    var node = ov;
    ov = null;
    setTimeout(function () {
      if (node) node.remove();
      document.documentElement.style.overflow = "";
      if (lastFocus && lastFocus.focus) {
        try {
          lastFocus.focus();
        } catch (e) {}
      }
    }, 240);
    if (escHandler) document.removeEventListener("keydown", escHandler, true);
  }

  function openModal(variant, via) {
    if (document.getElementById("pbis-ov")) return;
    injectCSS();
    lastFocus = document.activeElement;

    var img = (variant && variant.featured_image && variant.featured_image.src) || product.featured_image || "";
    if (img && img.indexOf("//") === 0) img = "https:" + img;
    var vLabel =
      variant && variant.title && variant.title !== "Default Title" ? variant.title : "";

    ov = document.createElement("div");
    ov.id = "pbis-ov";
    ov.innerHTML =
      '<div id="pbis-card" role="dialog" aria-modal="true" aria-labelledby="pbis-title">' +
      '<button id="pbis-x" type="button" aria-label="Close">&#10005;</button>' +
      '<div id="pbis-form">' +
      '<p class="pbis-eyebrow"><i></i>' + (isComingSoon() ? 'Coming soon' : 'Out of stock') + '</p>' +
      '<h2 id="pbis-title">Want one the moment it lands?</h2>' +
      '<p id="pbis-sub">Drop your email and we&rsquo;ll tell you <b>the minute this one ' + (isComingSoon() ? 'drops' : 'is back') + '</b> ' +
      "&mdash; before it goes out to everyone else.</p>" +
      '<div class="pbis-prod">' +
      (img ? '<img src="' + img + '" alt="">' : "") +
      "<div><strong>" +
      esc(product.title) +
      "</strong>" +
      (vLabel ? "<span>" + esc(vLabel) + "</span>" : "") +
      "</div></div>" +
      '<form id="pbis-formel" novalidate>' +
      '<div class="pbis-field">' +
      '<input id="pbis-email" type="email" name="email" autocomplete="email" inputmode="email" ' +
      'spellcheck="false" required aria-describedby="pbis-err" placeholder=" ">' +
      '<label for="pbis-email">Email address</label></div>' +
      '<span id="pbis-err" role="alert"></span>' +
      '<button id="pbis-submit" type="submit">Notify me</button>' +
      "</form>" +
      '<p class="pbis-fine">We&rsquo;ll only use this to tell you about this ' + (isComingSoon() ? 'drop' : 'restock') + ' and Proton news. ' +
      'Unsubscribe any time. <a href="/policies/privacy-policy">Privacy</a>.</p>' +
      "</div>" +
      '<div id="pbis-done">' +
      '<div class="pbis-tick">&#10003;</div>' +
      '<h2 id="pbis-title2" style="margin:0 0 8px;font-size:23px;font-weight:800;color:#fff">You&rsquo;re on the list.</h2>' +
      '<p id="pbis-sub" style="margin:0">We&rsquo;ll email you as soon as <b>' +
      esc(product.title) +
      "</b>" +
      (vLabel ? " (" + esc(vLabel) + ")" : "") +
      (isComingSoon() ? " drops.</p>" : " is back.</p>") +
      "</div></div>";
    document.body.appendChild(ov);

    card = document.getElementById("pbis-card");
    input = document.getElementById("pbis-email");
    err = document.getElementById("pbis-err");
    submit = document.getElementById("pbis-submit");

    // Prefill for a known customer — asking someone we can already identify to retype their
    // address is friction for no gain.
    var known = (window.PROTON_WELCOME_CTX && window.PROTON_WELCOME_CTX.customerEmail) || "";
    if (known) {
      input.value = known;
      input.classList.add("pbis-has");
    }

    document.getElementById("pbis-x").addEventListener("click", closeModal);
    ov.addEventListener("mousedown", function (e) {
      if (e.target === ov) closeModal();
    });
    document.getElementById("pbis-formel").addEventListener("submit", function (e) {
      onSubmit(e, variant);
    });
    input.addEventListener("input", function () {
      input.classList.toggle("pbis-has", !!input.value);
      err.textContent = "";
    });
    escHandler = function (e) {
      if (e.key === "Escape") closeModal();
    };
    document.addEventListener("keydown", escHandler, true);

    requestAnimationFrame(function () {
      ov.classList.add("pbis-in");
      // Desktop only — see the welcome popup: autofocus on a phone throws up the keyboard
      // and buries the CTA.
      if (window.matchMedia("(min-width:760px)").matches) {
        setTimeout(function () {
          try {
            input.focus({ preventScroll: true });
          } catch (e) {}
        }, 280);
      }
    });
    log("modal open via", via, variant && variant.id);
  }

  function esc(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }

  /* ── Klaviyo write path ───────────────────────────────────────────────────
   * 🚨 WE USE THE LEGACY BROWSER ENDPOINTS ON PURPOSE. DO NOT "MODERNISE" THIS.
   *
   * Klaviyo's current `/client/*` API requires a `revision` HEADER (400 without it; it
   * cannot travel as a query param). A custom header makes the request non-simple, so the
   * browser sends a CORS preflight — and the preflight for this account answers
   * `Access-Control-Allow-Methods: OPTIONS`, with no POST. Chrome therefore blocks the
   * request outright: "No 'Access-Control-Allow-Origin' header is present."
   * Measured 2026-09-09; it silently cost us desktop signups.
   *
   * These two legacy endpoints are what Klaviyo's own onsite forms use. Both are CORS
   * SIMPLE requests (form-encoded POST / plain GET, no custom headers) so no preflight is
   * ever issued, and both answer `access-control-allow-origin: https://protonsports.com`.
   * Verified end to end: consent lands as SUBSCRIBED, double_optin false, and the event
   * arrives with its properties intact.
   * ───────────────────────────────────────────────────────────────────────── */
  function klSubscribe(email, listId, source, extra) {
    var body = new URLSearchParams();
    body.set("g", listId);
    body.set("email", email);
    var fields = ["$source"];
    body.set("$source", source);
    for (var k in extra || {}) {
      fields.push(k);
      body.set(k, String(extra[k]));
    }
    body.set("$fields", fields.join(","));
    return fetch("https://manage.kmail-lists.com/ajax/subscriptions/subscribe", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: body.toString(),
    }).then(function (r) {
      if (!r.ok) throw new Error("subscribe " + r.status);
      return r.json();
    }).then(function (j) {
      // `success:false` comes back with a 200, so the status alone is not proof.
      if (!j || j.success !== true) throw new Error("subscribe rejected " + JSON.stringify(j && j.errors));
      return j;
    });
  }

  function klTrack(eventName, email, props) {
    var payload = {
      token: KLAVIYO_COMPANY_ID,
      event: eventName,
      customer_properties: { $email: email },
      properties: props || {},
    };
    // btoa() throws on non-ASCII (product titles carry ™, é, …) — encode to UTF-8 first.
    var b64 = btoa(unescape(encodeURIComponent(JSON.stringify(payload))));
    return fetch("https://a.klaviyo.com/api/track?data=" + encodeURIComponent(b64), {
      method: "GET",
    }).then(function (r) {
      return r.text();
    }).then(function (t) {
      if (t.trim() !== "1") throw new Error("track rejected: " + t.slice(0, 60));
      return t;
    });
  }

  /* ── submit ─────────────────────────────────────────────────────────────── */
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i;

  function onSubmit(e, variant) {
    e.preventDefault();
    var email = (input.value || "").trim();
    if (!EMAIL_RE.test(email)) {
      err.textContent = "Please enter a valid email address.";
      input.focus();
      return;
    }
    submit.disabled = true;
    submit.textContent = "Saving…";

    var props = {
      ProductTitle: product.title,
      ProductHandle: product.handle,
      ProductID: String(product.id),
      VariantID: variant ? String(variant.id) : null,
      VariantTitle: variant ? variant.title : null,
      SKU: variant ? variant.sku : null,
      Price: variant ? variant.price / 100 : null,
      ProductURL: location.origin + "/products/" + product.handle + (variant ? "?variant=" + variant.id : ""),
      ImageURL: (variant && variant.featured_image && variant.featured_image.src) || product.featured_image || null,
      RequestedAt: new Date().toISOString(),
    };

    // TWO writes, and both matter:
    //  1. list subscribe = the marketing CONSENT record
    //  2. track event    = WHICH variant they want. A profile property could not express
    //     this — one person can wait on several products at once and a property would
    //     overwrite the previous request. Events are append-only.
    // 🚨 The EVENT is the load-bearing half: it is the only record the restock notifier can
    //    read to decide who to email. Consent without it is a subscriber nobody can serve.
    Promise.allSettled([
      klSubscribe(email, KLAVIYO_LIST_ID, "Back In Stock", {
        bis_last_product: product.handle,
        bis_last_variant: variant ? String(variant.id) : "",
      }),
      klTrack(METRIC, email, props),
    ]).then(function (r) {
      var subOK = r[0].status === "fulfilled";
      var evOK = r[1].status === "fulfilled";
      log("subscribe", r[0].status, "| event", r[1].status);
      if (!subOK || !evOK) {
        writeState({
          failedAt: Date.now(),
          failedProduct: product.handle,
          failedWhich: (subOK ? "" : "subscribe ") + (evOK ? "" : "event"),
        });
      }
      succeed(variant);
    });
  }

  function succeed(variant) {
    card.classList.add("pbis-done");
    var st = readState();
    var asked = st.asked || {};
    asked[keyFor(variant)] = { at: Date.now(), signed: true };
    writeState({ asked: asked });
    try {
      document.dispatchEvent(
        new CustomEvent("proton:bis-signup", { detail: { handle: product.handle, variant: variant && variant.id } })
      );
    } catch (e) {}
  }

  function keyFor(variant) {
    return product.handle + ":" + (variant ? variant.id : "0");
  }

  /* ── the reconcile loop — UI is a pure function of live stock ────────────── */
  function reconcile() {
    var ex = isExcluded();
    if (ex) {
      removeInline();
      window.__PROTON_BIS_PENDING = false;
      log("excluded:", ex);
      return;
    }
    var vid = detectVariantId();
    var variant = variantById(vid);
    currentVariantId = vid;

    if (!variant) {
      removeInline();
      window.__PROTON_BIS_PENDING = false;
      return;
    }

    // 🚨 Stake the claim EARLY. The welcome popup fires on a 6s dwell and this modal waits
    // 7s, so without this flag the generic "10% off" prompt wins the overlay slot on a
    // sold-out PDP and the back-in-stock offer never appears at all — which is exactly
    // backwards: someone staring at a sold-out paddle has a specific, higher-intent need
    // than a first-time visitor. Set at DETECTION time (~1-2s), not at display time.
    window.__PROTON_BIS_PENDING = !variant.available;

    if (variant.available) {
      // 🚨 THIS BRANCH IS THE "get rid of the pop-up" REQUIREMENT. It needs no restock job
      // and no cleanup task: the moment Shopify reports the variant available, the button
      // is removed and the modal is never opened. There is no stored "is out of stock"
      // flag anywhere that could disagree with reality.
      removeInline();
      if (ov) closeModal();
      log("variant available — nothing to show", vid);
      return;
    }

    log("variant OUT of stock", vid, variant.title);
    mountInline(variant);
    if (isComingSoon()) return;   // coming soon: the inline card IS the signup — no auto-modal on top of it

    // The modal is once per product per browser per cooldown. The inline button stays
    // forever, so suppressing the modal never costs the customer the ability to sign up —
    // which is what makes an aggressive-feeling interruption acceptable at all.
    if (modalShownThisPage && !FORCE) return;
    var st = readState();
    var asked = st.asked || {};
    var prev = asked[keyFor(variant)];
    if (!FORCE && prev && Date.now() - prev.at < MODAL_COOLDOWN_DAYS * 864e5) {
      log("modal suppressed — already asked", prev.signed ? "(signed up)" : "(dismissed)");
      return;
    }
    modalShownThisPage = true;
    setTimeout(function () {
      // Re-check at fire time: the shopper may have switched to an in-stock variant during
      // the delay, and opening then would be flatly wrong.
      var again = variantById(detectVariantId());
      if (!again || again.available) return log("aborted — variant became available");
      if (isExcluded()) return;
      asked[keyFor(again)] = { at: Date.now(), signed: false };
      writeState({ asked: asked });
      openModal(again, "auto");
    }, FORCE ? 200 : MODAL_DELAY_MS);
  }

  /* ── watch for variant changes ──────────────────────────────────────────── */
  function watch() {
    var lastUrl = location.href;
    // Dawn rewrites ?variant= on selection; that is the most reliable signal.
    setInterval(function () {
      if (location.href !== lastUrl) {
        lastUrl = location.href;
        var v = detectVariantId();
        if (v !== currentVariantId) {
          log("variant changed via URL ->", v);
          reconcile();
        }
      }
    }, 400);
    document.addEventListener("change", function (e) {
      if (e.target && e.target.closest && e.target.closest('form[action*="/cart/add"], variant-selects, variant-radios')) {
        setTimeout(reconcile, 120);
      }
    });
    // Some themes swap the buy box wholesale; re-mount if our button vanished while the
    // variant is still unavailable.
    var mo = new MutationObserver(function () {
      if (!product) return;
      var v = variantById(currentVariantId);
      if (v && !v.available && !document.getElementById("pbis-inline")) mountInline(v);
    });
    if (document.body) mo.observe(document.body, { childList: true, subtree: true });
  }

  /* ── boot ───────────────────────────────────────────────────────────────── */
  function start() {
    var handle = handleFromPath();
    if (!handle) return;
    if (window.Shopify && window.Shopify.designMode) return log("design mode");
    loadProduct(handle)
      .then(function (p) {
        product = p;
        log("loaded", p.handle, p.variants.length, "variants,", p.variants.filter(function (v) { return !v.available; }).length, "out");
        reconcile();
        watch();
      })
      .catch(function (e) {
        // Fail SILENT and CLOSED. A product fetch that errors must never be treated as
        // "out of stock" — that would show a restock form on a buyable paddle.
        log("load failed — showing nothing", e);
      });
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", start);
  else start();

  window.ProtonBIS = {
    show: function () {
      openModal(variantById(currentVariantId), "manual");
    },
    state: readState,
    product: function () {
      return product;
    },
    reset: function () {
      try {
        localStorage.removeItem(STORE_KEY);
      } catch (e) {}
    },
  };
})();
